// Units controller — extracted verbatim from GameScene.ts (FACE&FURY S2 SPLIT).
// Behavior-frozen: method bodies are byte-identical to the monolith; only the signatures were
// rewritten to `export function name(this: GameScene, ...)` and bound back onto the scene as fields.
import Phaser from 'phaser';
import type { Snapshot } from '../../core';
import { SCORING } from '../../core';
import { PALETTE, hex, FONT, tex, hasTex, muncherArtReady } from '../keys';
import { shake, particleBurst, glowPulse, squashStretch, shockwaveRing, createCometTrail, floatText } from '../juice';
import { audio } from '../audio';
import { colorblindActive, motionAllowed } from '../settings';
import { drawPlayerMuncher, drawRivalMuncher, applyCosmeticSkin, type SkinLook } from '../muncher';
import { selectedCosmetic } from '../cosmetics';
import { SURGE_CORE, SURGE_EDGE, PLAYER, AI } from './types';
import type { GameScene } from '../scenes/GameScene';

// ── S9 LANE-G1 #40 HAZARD IDENTITY ─────────────────────────────────────────────────────────────
/** #40: moving Glitches previously shared the pink-red PALETTE.hazard used by a decoy-EAT MISTAKE, so
 *  "did I misfire or did a hazard hit me?" was ambiguous. Give the PERSISTENT hazard identity (danger
 *  aura + targeting reticle + procedural fallback shapes) a distinct HAZARD-ORANGE. The transient
 *  pre-strike / life-loss RED flashes stay red (universal "imminent danger"); the steady orange-auraed,
 *  reticled glitch tokens are the CLASS cue that disambiguates them from a momentary red mistake flash.
 *  Double-coded by the hazards' distinct SHAPES + the reticle, so it never leans on hue alone. */
const HAZARD_THREAT = 0xff7a1a;
/** Base alpha for the four-corner targeting reticle that rings each hazard (breathes under motion). */
const HAZARD_RETICLE_ALPHA = 0.55;
/** Rival pre-move wind-up. Long enough to read in 25fps review video without making BYT3 feel sluggish. */
const RIVAL_MOVE_TELEGRAPH_MS = 280;

function rivalMissEdgePulse(scene: GameScene, durMs = 680, peakAlpha = 0.26): void {
  const cam = scene.cameras.main;
  const w = cam.width;
  const h = cam.height;
  const topSafe = Math.min(104, Math.max(76, h * 0.12));
  const thick = Math.max(18, Math.min(w, h) * 0.055);
  const alpha = motionAllowed() ? peakAlpha : peakAlpha * 0.5;
  const g = scene.add.graphics().setScrollFactor(0).setDepth(1.1).setAlpha(0);
  g.fillStyle(PALETTE.ai, 1);
  g.fillRect(0, topSafe, thick, h - topSafe);
  g.fillRect(w - thick, topSafe, thick, h - topSafe);
  g.fillRect(0, h - thick, w, thick);
  scene.tweens.add({
    targets: g,
    alpha: { from: 0, to: alpha },
    duration: durMs * 0.32,
    ease: 'Quad.easeOut',
    yoyo: true,
    hold: durMs * 0.14,
    onComplete: () => g.destroy(),
  });
}

const HAZARD_LABEL: Record<string, { text: string; color: number }> = {
  crawler: { text: 'CRAWL', color: HAZARD_THREAT },
  hunter: { text: 'HUNT', color: HAZARD_THREAT },
  liar: { text: 'LIAR', color: PALETTE.ai },
  scrambler: { text: 'SCRAM', color: PALETTE.scan },
};



  // --- munchers ------------------------------------------------------------
export function buildMunchers(this: GameScene, snap: Snapshot): void {
    const human = snap.players.find((p) => p.id === PLAYER);
    this.humanSprite = this.makeHumanSprite();
    if (human) {
      const c = this.cellCenter(human.pos);
      this.humanSprite.setPosition(c.x, c.y);
    }
    this.humanSprite.setDepth(6);

    this.aiSprite = this.makeAiSprite();
    const ai = snap.players.find((p) => p.id === AI);
    if (ai) {
      const c = this.cellCenter(ai.pos);
      this.aiSprite.setPosition(c.x, c.y);
    }
    this.aiSprite.setVisible(this.aiEnabled).setDepth(5);
    if (this.aiEnabled) this.aiPressureCue = this.add.graphics().setDepth(4.75);

    // WARN-4: register both munchers' bobbable bodies + start the single idle-life handler.
    this.registerIdleMuncher(this.humanSprite, 0);
    if (this.aiEnabled) this.registerIdleMuncher(this.aiSprite, Math.PI); // out-of-phase from the human
    this.startMuncherIdle();
  }


  /**
   * WARN-4: capture a muncher's bobbable BODY child (the art image, else the procedural body shape) and
   * its rest-y so the idle handler can bob it. No-op if the sprite has no body handle.
   */
export function registerIdleMuncher(this: GameScene, sprite: Phaser.GameObjects.Container, phase: number): void {
    const art = sprite.getData('art') as Phaser.GameObjects.Image | undefined;
    const proc = sprite.getData('body') as Phaser.GameObjects.GameObject | undefined;
    const body = (art ?? proc) as
      | (Phaser.GameObjects.GameObject & { y: number; setY: (v: number) => unknown })
      | undefined;
    if (!body) return;
    this.idleMunchers.push({ container: sprite, body, restY: body.y, phase });
  }


  /**
   * WARN-4 MUNCHER IDLE LIFE — a single per-frame UPDATE handler that gently bobs each muncher's body
   * (a tiny vertical offset around its rest-y, on the BODY child only) so the munchers feel alive at
   * rest. It is purely cosmetic: it NEVER touches the container's grid position (moveSprite owns that),
   * and bobs the y of a child that nothing else animates (muncherBitePop pops the art SCALE; squash
   * deforms the CONTAINER) so the layers never fight. While a muncher is mid-STEP (its container has an
   * active position tween) the bob eases toward zero so movement reads cleanly. Skipped entirely under
   * reduced-motion (bodies sit flat at rest-y). The handler is unbound on SHUTDOWN.
   */
export function startMuncherIdle(this: GameScene): void {
    if (!motionAllowed() || this.idleMunchers.length === 0) return;
    const onIdle = (_t: number, dt: number): void => {
      this.idleMuncherElapsed += dt;
      const s = this.idleMuncherElapsed / 1000;
      const r = this.munchRadius();
      const amp = Math.max(1, r * 0.12); // a hair of bob — alive, never enough to hurt legibility.
      for (const m of this.idleMunchers) {
        if (!m.body.active || !m.container.active) continue;
        // Suppress the bob mid-step so a moving muncher reads as travelling, not floating; ease back in.
        const moving = this.tweens.getTweensOf(m.container).length > 0;
        const target = moving ? 0 : amp * Math.sin(s * 2.4 + m.phase); // ~0.4 Hz gentle breath/bob
        // critically-damped-ish lerp toward the target so transitions in/out of movement are smooth.
        const cur = m.body.y - m.restY;
        m.body.setY(m.restY + cur + (target - cur) * 0.18);
      }
    };
    this.events.on(Phaser.Scenes.Events.UPDATE, onIdle, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off(Phaser.Scenes.Events.UPDATE, onIdle, this);
      // settle bodies back to their rest-y so a teardown leaves no residual offset.
      for (const m of this.idleMunchers) {
        if (m.body.active) m.body.setY(m.restY);
      }
    });
  }


  // M1/S9 occlusion fix: shrink the sprite (was 0.32) so the cell label it stands on stays readable,
  // and push it toward the BOTTOM of the cell so the centered answer text clears the token. A thin dark
  // outline keeps the smaller sprite legible against any tile.
export function munchRadius(this: GameScene): number {
    return Math.max(8, Math.min(this.cellW, this.cellH) * 0.24);
  }


  /** Sprites sit low in the cell so the label (centered) reads above them. */
export function spriteYOffset(this: GameScene): number {
    return Math.min(this.cellW, this.cellH) * 0.25;
  }


  /**
   * Add a soft contact shadow under a sprite so it sits ON the board instead of floating. A low-alpha
   * dark ellipse, wider than tall, parked at the sprite's BASE (just below + behind the body). It is
   * added as the FIRST child of the container so it renders behind everything else, and because it
   * lives inside the container it tracks the sprite automatically through moveSprite()'s tween. Static
   * (created once); no per-frame work. Returns the ellipse in case a caller wants it.
   */
export function addSpriteShadow(this: GameScene, c: Phaser.GameObjects.Container, radius: number): Phaser.GameObjects.Ellipse {
    const yo = this.spriteYOffset();
    const shadow = this.add
      .ellipse(0, yo + radius * 0.82, radius * 2.0, radius * 0.9, 0x000000, 0.32)
      .setOrigin(0.5);
    c.addAt(shadow, 0);
    return shadow;
  }


  /**
   * HUD-scale separation halo: the art muncher's dark backing disc (makeArtSprite) blends INTO the
   * dark board, so at HUD scale the two munchers read muddy. A soft accent-color halo behind the body
   * lifts the silhouette off dark tiles, while the dark backing still outlines it on bright/eaten
   * tiles — best of both. Two concentric ADDITIVE discs give a gentle falloff; inserted just above the
   * contact shadow (index 1) so they sit BEHIND the backing + art, never over the cell label. Static
   * (no per-frame work) and color-coded (cyan=YOU / magenta=AI), reinforcing the YOU/AI double-coding.
   */
export function addSeparationGlow(this: GameScene, c: Phaser.GameObjects.Container, radius: number, color: number): void {
    const yo = this.spriteYOffset();
    const outer = this.add
      .circle(0, yo, radius * 1.4, color, 0.12)
      .setBlendMode(Phaser.BlendModes.ADD);
    const inner = this.add
      .circle(0, yo, radius * 1.12, color, 0.17)
      .setBlendMode(Phaser.BlendModes.ADD);
    // addAt(inner,1) then addAt(outer,1) => render order shadow, outer, inner, backing, art:
    // the larger/fainter disc sits behind the smaller/brighter one for a soft rim.
    c.addAt(inner, 1);
    c.addAt(outer, 1);
  }


  /**
   * Rival intent pulse: a faint magenta oval that breathes under the AI body even when the Core keeps
   * it waiting on a cell. This keeps the duel readable as "the rival is active" without inventing
   * renderer-only movement or covering answer labels.
   */
export function addRivalIntentPulse(this: GameScene, c: Phaser.GameObjects.Container, radius: number): void {
    const yo = this.spriteYOffset();
    const pulse = this.add
      .ellipse(0, yo + radius * 0.1, radius * 2.45, radius * 1.42)
      .setStrokeStyle(2, PALETTE.ai, motionAllowed() ? 0.46 : 0.38)
      .setBlendMode(Phaser.BlendModes.ADD);
    c.addAt(pulse, 1);
    if (!motionAllowed()) return;
    this.tweens.add({
      targets: pulse,
      alpha: { from: 0.68, to: 0.2 },
      scaleX: { from: 0.9, to: 1.2 },
      scaleY: { from: 0.92, to: 1.1 },
      duration: 960,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }


  /**
   * Build an art-backed sprite sized to FIT the (small, low-sitting) muncher slot so it preserves the
   * M1 occlusion fix: the image is scaled to ~2*radius and pushed to spriteYOffset() so the centered
   * cell label still peeks above it. A soft dark backing disc keeps the sprite legible on any tile.
   * Returns the Container; the Image is stored as the container's data 'art' so frames can be swapped.
   */
export function makeArtSprite(this: GameScene, logicalKey: Parameters<typeof tex>[0], radius: number): Phaser.GameObjects.Container {
    const c = this.add.container(0, 0);
    const yo = this.spriteYOffset();
    const backing = this.add.circle(0, yo, radius + 2, PALETTE.bg, 0.6);
    const img = this.add.image(0, yo, tex(logicalKey));
    this.fitImageTo(img, radius * 2.2);
    c.add([backing, img]);
    c.setData('art', img);
    // Contact shadow under the sprite (added at index 0 -> behind backing/art) so it grounds on the board.
    this.addSpriteShadow(c, radius);
    return c;
  }


  /** Scale a (square-ish) image so its larger side spans `target` px, keeping aspect ratio. */
export function fitImageTo(this: GameScene, img: Phaser.GameObjects.Image, target: number): void {
    const w = img.width || 1;
    const h = img.height || 1;
    img.setScale(target / Math.max(w, h));
  }


  /** Swap the texture of an art-backed sprite (frame change) and re-fit it to the muncher slot. */
export function setSpriteFrame(this: GameScene, sprite: Phaser.GameObjects.Container, logicalKey: Parameters<typeof tex>[0]): void {
    const img = sprite.getData('art') as Phaser.GameObjects.Image | undefined;
    if (!img || !hasTex(logicalKey)) return;
    img.setTexture(tex(logicalKey));
    this.fitImageTo(img, this.munchRadius() * 2.2);
  }


  /**
   * Pulse a one-shot animation frame on an art-backed muncher (e.g. chomp/hurt), then auto-revert to
   * its resting frame after `ms`. No-op when art isn't wired (procedural fallback keeps its own juice).
   */
export function pulseSpriteFrame(this: GameScene,
    sprite: Phaser.GameObjects.Container,
    frameKey: Parameters<typeof tex>[0],
    restKey: Parameters<typeof tex>[0],
    ms = 150,
  ): void {
    const img = sprite.getData('art') as Phaser.GameObjects.Image | undefined;
    if (!img || !hasTex(frameKey)) return;
    sprite.setData('__actionFrameUntil', this.time.now + ms + 40);
    this.setSpriteFrame(sprite, frameKey);
    this.time.delayedCall(ms, () => {
      if (sprite.active) this.setSpriteFrame(sprite, restKey);
    });
  }


  /**
   * Give movement a visible art-frame beat using the accepted muncher frames we already ship. This
   * keeps the renderer inside the asset contract while making travel read as a distinct state instead
   * of pure container interpolation. It deliberately yields to bite/hurt/win reaction frames.
   */
export function pulseMoveSpriteFrame(this: GameScene, sprite: Phaser.GameObjects.Container, cellId: number, ms: number): void {
    const actionUntil = Number(sprite.getData('__actionFrameUntil') ?? 0);
    if (actionUntil > this.time.now) return;
    const frameKey =
      sprite === this.humanSprite ? 'playerMouthClosed' : sprite === this.aiSprite ? 'aiChomp' : null;
    const restKey = sprite === this.humanSprite ? 'playerIdle' : sprite === this.aiSprite ? 'aiIdle' : null;
    if (!frameKey || !restKey || !hasTex(frameKey)) return;
    const token = `${cellId}:${Math.round(this.time.now)}`;
    sprite.setData('__moveFrameToken', token);
    this.setSpriteFrame(sprite, frameKey);
    this.time.delayedCall(Math.max(90, Math.min(180, ms + 30)), () => {
      if (!sprite.active || sprite.getData('__moveFrameToken') !== token) return;
      const stillAction = Number(sprite.getData('__actionFrameUntil') ?? 0) > this.time.now;
      if (!stillAction) this.setSpriteFrame(sprite, restKey);
    });
  }


  /** Add a tiny travel squash so board-to-board movement reads as animated, not just translated. */
export function pulseMoveSquash(this: GameScene, sprite: Phaser.GameObjects.Container, cellId: number, ms: number): void {
    if (!motionAllowed()) return;
    const actionUntil = Number(sprite.getData('__actionFrameUntil') ?? 0);
    if (actionUntil > this.time.now) return;
    const token = `${cellId}:${Math.round(this.time.now)}`;
    // Rest-scale anchor (verifier WARN): the squash cycle (~135ms) outlives a glide step (90ms), so
    // capturing LIVE scale mid-yoyo ratcheted the restore value permanently. Anchor the true rest
    // scale once per sprite lifetime; every squash targets/restores relative to THAT. (If a sprite's
    // legit rest scale ever changes post-creation, reset these data keys at that site.)
    const baseX = Number(sprite.getData('__restScaleX') ?? 0) || sprite.scaleX || 1;
    const baseY = Number(sprite.getData('__restScaleY') ?? 0) || sprite.scaleY || 1;
    sprite.setData('__restScaleX', baseX);
    sprite.setData('__restScaleY', baseY);
    sprite.setData('__moveScaleToken', token);
    // Amplitude raised ~37% (1.12/0.88 -> 1.17/0.84): the old squash read as sub-pixel at 540p/25fps
    // capture, so movement looked like pure translation. Still yoyo'd within one step, label-safe.
    this.tweens.add({
      targets: sprite,
      scaleX: baseX * 1.17,
      scaleY: baseY * 0.84,
      duration: Math.max(64, Math.min(108, ms * 0.52)),
      ease: 'Quad.easeOut',
      yoyo: true,
      onComplete: () => {
        if (!sprite.active || sprite.getData('__moveScaleToken') !== token) return;
        sprite.setScale(baseX, baseY);
      },
    });
  }


  /**
   * VG-8: BITE RECOIL — a tight, NON-collapsing scale-pop (~1.0 -> 1.12 -> 1.0 over ~140ms) for tactile
   * "chomp" weight on a correct eat. It pops the muncher's ART IMAGE child (a target SEPARATE from the
   * container that squashStretch deforms), so the two layer cleanly instead of fighting — and it leaves
   * the separation glow / contact shadow / backing (other container children) untouched, so the muncher
   * still reads cleanly with no engulfing. Footprint kept tight (peak ~1.12, threaded ~1.18) so it never
   * bloats the small sprite. Motion-gated; no-op on the procedural (no-art) muncher, whose own squash +
   * mouth-wedge already carry the weight (there's no stable child handle to pop without fighting squash).
   */
export function muncherBitePop(this: GameScene, sprite: Phaser.GameObjects.Container, threaded: boolean): void {
    if (!motionAllowed()) return;
    const img = sprite.getData('art') as Phaser.GameObjects.Image | undefined;
    if (!img || !img.active) return;
    const base = img.scale || 1;
    // Peaks raised (1.12/1.18 -> 1.2/1.3): the bite recoil was too subtle to read at phone size —
    // the eat moment is the game's signature verb, so it must visibly "chomp", not tick.
    const peak = base * (threaded ? 1.3 : 1.2);
    this.tweens.add({
      targets: img,
      scale: { from: base, to: peak },
      duration: threaded ? 70 : 56,
      ease: 'Quad.easeOut',
      yoyo: true,
      onComplete: () => {
        if (img.active) img.setScale(base); // settle exactly back to base (never collapse to 0).
      },
    });
  }


  /**
   * Attach the ANIMATED raster muncher sprite (AutoSprite idle/chomp/hurt/win) to a muncher container
   * and start its idle loop. Returns false when the sheet didn't decode (headless / missing) so the
   * caller keeps the geometric fallback. Renderer-only; no Core/determinism touch. A per-sprite neon
   * bloom (WebGL only) gives the OLED glow the flat geometric body lacked, kept surgical so HUD text
   * stays crisp.
   */
export function attachMuncherSprite(this: GameScene, c: Phaser.GameObjects.Container, kind: 'player' | 'rival', skin?: SkinLook): boolean {
    if (!muncherArtReady(this)) return false;
    const idleKey = kind === 'player' ? 'msPlayerIdle' : 'msRivalIdle';
    const idleAnim = kind === 'player' ? 'mun-player-idle' : 'mun-rival-idle';
    if (!this.textures.exists(idleKey)) return false;
    const r = this.munchRadius();
    const spr = this.add.sprite(0, this.spriteYOffset(), idleKey);
    const restScale = (r * 2.4) / Math.max(spr.width || 1, spr.height || 1);
    spr.setScale(restScale);
    spr.setData('restScale', restScale); // the TRUE resting scale, so hurt/win pops always restore exactly
    // Cosmetic skin: WebGL hue-rotation (true named color) or multiply-tint fallback. baseTint is
    // only set on the tint path — hurt/win flash restores must not clobber the FX with a stale tint.
    const usedFx = skin ? applyCosmeticSkin(spr, skin) : false;
    spr.setData('baseTint', !usedFx && skin?.tint != null ? skin.tint : null);
    if (this.renderer.type === Phaser.WEBGL) {
      try {
        // Identity bloom in the SKIN color (else the default player/rival identity color).
        spr.postFX.addBloom(skin?.tint ?? (kind === 'player' ? PALETTE.player : PALETTE.ai), 1, 1, 1, 1.1, 4);
      } catch {
        /* FX pipeline unsupported — the flat sprite still reads fine */
      }
    }
    if (this.anims.exists(idleAnim)) {
      try {
        // reduceMotion invariant: hold a single static frame instead of the looping idle (mirrors the
        // geometric muncher, whose idle bob is suppressed under reduced-motion). Reactions gated too.
        if (motionAllowed()) spr.play(idleAnim);
        else spr.setFrame(0);
      } catch {
        /* ignore */
      }
    }
    c.add(spr);
    c.setData('animSprite', spr);
    c.setData('animKind', kind);
    return true;
  }


  /**
   * Play a one-shot reaction anim (chomp / hurt / win / advance) on a muncher's animated sprite, then
   * return to its idle loop. No-op on the geometric fallback muncher (its own squash/juice carries it).
   */
export function playMuncherAnim(this: GameScene, c: Phaser.GameObjects.Container | undefined, animKey: string): void {
    if (!c || !motionAllowed()) return; // reduceMotion: muncher holds its static idle frame, no reaction anim
    const spr = c.getData('animSprite') as Phaser.GameObjects.Sprite | undefined;
    if (!spr || !spr.active || !this.anims.exists(animKey)) return;
    const idleAnim = c.getData('animKind') === 'rival' ? 'mun-rival-idle' : 'mun-player-idle';
    // Drop any prior pending return-to-idle listener so rapid reactions don't stack redundant handlers.
    spr.off(Phaser.Animations.Events.ANIMATION_COMPLETE);
    spr.play(animKey);
    spr.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      if (spr.active) spr.play(idleAnim);
    });
  }


  /** Restore a muncher sprite's resting tint (cosmetic base, else none) after a hurt/win flash. */
export function restoreMuncherTint(this: GameScene, spr: Phaser.GameObjects.Sprite): void {
    const base = spr.getData('baseTint') as number | null | undefined;
    if (base != null) spr.setTint(base);
    else spr.clearTint();
  }


  /**
   * HURT / WIN reaction on the animated muncher sprite — a brief accent TINT (+ a motion-gated pop)
   * over the idle loop. AutoSprite's dedicated hurt/win frames drifted off-model, so these reactions
   * are code-driven on the clean idle sprite; the existing flash/shake/confetti/VO carry the rest.
   * No-op on the geometric fallback muncher (its own juice already covers it).
   */
export function reactMuncher(this: GameScene, c: Phaser.GameObjects.Container | undefined, kind: 'hurt' | 'win'): void {
    if (!c) return;
    const spr = c.getData('animSprite') as Phaser.GameObjects.Sprite | undefined;
    if (!spr || !spr.active) return;
    // Use the TRUE resting scale (not the live, possibly mid-pop scale) so overlapping reactions never
    // drift the muncher's rest size; kill any in-flight pop before starting a new one.
    const restScale = (spr.getData('restScale') as number | undefined) ?? spr.scale;
    if (kind === 'hurt') {
      spr.setTint(PALETTE.hazard);
      this.time.delayedCall(260, () => {
        if (spr.active) this.restoreMuncherTint(spr);
      });
      if (motionAllowed()) {
        this.tweens.killTweensOf(spr);
        this.tweens.add({
          targets: spr,
          scaleX: restScale * 0.86,
          scaleY: restScale * 1.1,
          duration: 70,
          yoyo: true,
          ease: 'Quad.easeOut',
          onComplete: () => {
            if (spr.active) spr.setScale(restScale);
          },
        });
      }
    } else {
      spr.setTint(PALETTE.gold);
      this.time.delayedCall(520, () => {
        if (spr.active) this.restoreMuncherTint(spr);
      });
      if (motionAllowed()) {
        this.tweens.killTweensOf(spr);
        this.tweens.add({
          targets: spr,
          scale: restScale * 1.22,
          duration: 180,
          yoyo: true,
          ease: 'Back.easeOut',
          onComplete: () => {
            if (spr.active) spr.setScale(restScale);
          },
        });
      }
    }
  }


export function makeHumanSprite(this: GameScene): Phaser.GameObjects.Container {
    // Tactical-OLED: prefer the ANIMATED raster muncher (AutoSprite) when its sheet decoded; otherwise
    // fall back to the shared GEOMETRIC muncher (cyan pac wedge). P2.3 cosmetic skins recolor the body.
    const r = this.munchRadius();
    const c = this.add.container(0, 0);
    this.addSpriteShadow(c, r); // contact shadow grounds the muncher on the board.
    const cos = selectedCosmetic();
    if (!this.attachMuncherSprite(c, 'player', cos)) {
      const body = this.add.graphics({ x: 0, y: this.spriteYOffset() });
      drawPlayerMuncher(body, r / 0.42, { tint: cos.tint ?? PALETTE.player, glow: false });
      c.add(body);
      // WARN-4: tag the geometric body Graphics for the idle bob (the handler bobs ONLY this child).
      c.setData('body', body);
    }
    this.addSeparationGlow(c, r, PALETTE.player);
    // Owner-feedback protection/offense auras (all start hidden; driven each frame by updatePlayerAuras).
    const yo = this.spriteYOffset();
    // SAFE GROUND = a translucent green shield DOME over the muncher (bubble fill + bright rim), placed
    // IN FRONT of the body so it reads as an enclosing bubble; the low fill alpha keeps the face/label
    // legible. The YOU tag is added last so it always stays on top.
    this.safeGroundBubble = this.add.circle(0, yo, r * 1.32, PALETTE.correct, 0.16).setVisible(false);
    this.safeGroundRing = this.add
      .circle(0, yo, r * 1.32, PALETTE.correct, 0)
      .setStrokeStyle(Math.max(2.5, r * 0.16), PALETTE.correct, 0.95)
      .setVisible(false);
    // CHOMP SURGE = a deeper-blue core glow BEHIND the body + a white-hot edge ring IN FRONT of it, so
    // "you are powered up" is unmistakable; the spark emitter (updateSurgeSparks) adds the crackle texture.
    this.surgeCore = this.add
      .circle(0, yo, r * 1.06, SURGE_CORE, 0)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false);
    this.surgeRing = this.add
      .circle(0, yo, r * 1.22, SURGE_EDGE, 0)
      .setStrokeStyle(Math.max(2.25, r * 0.14), SURGE_EDGE, 0.95)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false);
    c.addAt(this.surgeCore, 1); // behind body (underglow core)
    c.add(this.safeGroundBubble); // in front of body (dome fill)
    c.add(this.safeGroundRing); // in front of body (dome rim)
    c.add(this.surgeRing); // in front of body (electric edge)
    this.addPlayerTag(c, 'YOU', PALETTE.player, r);
    return c;
  }


export function makeAiSprite(this: GameScene): Phaser.GameObjects.Container {
    const r = this.munchRadius();
    const c = this.add.container(0, 0);
    this.addSpriteShadow(c, r); // contact shadow behind the rival so it sits on the board.
    // S6 IDENTITY: the rival board token is now BYT3's shaded shard-arrow CREATURE (pose set), not the
    // flat magenta triangle. Priority: pose sprite → animated spritesheet → geometric triangle (the last
    // two remain only as the headless / decode-failure fallback, so a real headed frame always shows the
    // creature). buildMunchers registers the bobbable BODY (getData('body') set below on the fallbacks;
    // the pose path sets 'body' to its own image inside attachRivalPoseSprite).
    if (!this.attachRivalPoseSprite(c) && !this.attachMuncherSprite(c, 'rival')) {
      const body = this.add.graphics({ x: 0, y: this.spriteYOffset() });
      drawRivalMuncher(body, r / 0.46, { glow: false });
      c.add(body);
      c.setData('body', body); // WARN-4: geometric rival body the idle handler bobs.
    }
    this.addSeparationGlow(c, r, PALETTE.ai);
    this.addRivalIntentPulse(c, r);
    this.addPlayerTag(c, 'AI', PALETTE.ai, r);
    // Rival presence (Gemini minor): +9% container scale so the antagonist reads as a peer, not a
    // background mote. Move/eat deforms capture the live scale as their base, so they restore to it.
    c.setScale(1.09);
    this.rivalBasePose = 'idle';
    return c;
  }


  // --- S6 rival board-token pose set (BYT3 shard-arrow creature) ---------------------------------
  /** Gem registration point (normalized) shared by every 256px pose frame: gem at (300,380) of the
   *  600px source ⇒ (0.5, 0.6333). Anchoring the sprite ORIGIN on the gem (not bbox-center) keeps the
   *  token from jumping when the pose swaps, even though the four bodies fill the frame differently. */
export const RIVAL_GEM_Y = 380 / 600;
  /** Frame display footprint = munchRadius × this. The pose must read as a rival token while preserving
   *  the top-pinned answer label lane on the occupied tile. */
const RIVAL_POSE_FIT = 2.38;
export const RIVAL_POSE_KEYS = {
  idle: 'rivalPoseIdle',
  taunt: 'rivalPoseTaunt',
  chomp: 'rivalPoseChomp',
  defeat: 'rivalPoseDefeat',
} as const;
export type RivalPoseState = keyof typeof RIVAL_POSE_KEYS;

  /** True only when ALL four pose textures decoded — all-or-nothing so a partial set never mixes with
   *  the triangle mid-board (mirrors the emote-portrait gate). */
export function rivalPosesReady(this: GameScene): boolean {
    return (
      hasTex(RIVAL_POSE_KEYS.idle) &&
      hasTex(RIVAL_POSE_KEYS.taunt) &&
      hasTex(RIVAL_POSE_KEYS.chomp) &&
      hasTex(RIVAL_POSE_KEYS.defeat)
    );
  }

  /** Scale a pose frame to its cell footprint (called on build + every pose swap; all frames are 256²
   *  so the scale is constant, but re-fitting is cheap insurance if a source dimension ever changes). */
export function fitRivalPose(this: GameScene, img: Phaser.GameObjects.Image, r: number): void {
    const target = r * RIVAL_POSE_FIT;
    img.setScale(target / Math.max(img.width || 1, img.height || 1));
  }

  /**
   * Attach the gem-locked pose Image to the rival container (idle default). NO rotation — direction is
   * read via the comet trail / lock-on beam / arrival pulse, exactly as before, so the arrowhead never
   * spins. Returns false (adds nothing) when the pose set isn't fully registered, so makeAiSprite falls
   * through to the spritesheet/triangle. The pose Image is tagged as the container's bobbable 'body'.
   */
export function attachRivalPoseSprite(this: GameScene, c: Phaser.GameObjects.Container): boolean {
    if (!this.rivalPosesReady()) return false;
    const img = this.add
      .image(0, this.spriteYOffset() + this.munchRadius() * 0.36, tex(RIVAL_POSE_KEYS.idle))
      .setOrigin(0.5, RIVAL_GEM_Y);
    this.fitRivalPose(img, this.munchRadius());
    img.setData('poseState', 'idle');
    c.add(img);
    c.setData('rivalPose', img);
    c.setData('body', img); // idle bob targets the pose image (WARN-4)
    return true;
  }

  /**
   * Set the rival's board-token pose. State map (S6): idle = default; taunt = rival holds the lead /
   * gloats; chomp = a rival munch event (transient ~600ms, then reverts to the base pose); defeat = the
   * rival LOST this board. Non-transient states latch as `rivalBasePose`; a transient chomp reverts to
   * whatever the base is. No-op (procedural fallback keeps its own juice) when the pose set isn't wired.
   * Motion-gated pop on a real state change; reduceMotion just swaps the frame. Gem stays anchored.
   */
export function setRivalPose(this: GameScene, state: RivalPoseState, transient = false): void {
    const c = this.aiSprite;
    if (!c || !c.active) return;
    if (!transient) this.rivalBasePose = state;
    const img = c.getData('rivalPose') as Phaser.GameObjects.Image | undefined;
    if (!img || !img.active) return;
    const key = RIVAL_POSE_KEYS[state];
    if (!hasTex(key)) return;
    if (img.getData('poseState') !== state) {
      img.setTexture(tex(key));
      img.setData('poseState', state);
      this.fitRivalPose(img, this.munchRadius());
      if (motionAllowed()) {
        const base = img.scale;
        this.tweens.killTweensOf(img); // never stack pose pops (a fast chomp burst)
        img.setScale(base * (state === 'chomp' ? 1.16 : 1.1));
        this.tweens.add({
          targets: img,
          scale: base,
          duration: state === 'chomp' ? 150 : 240,
          ease: 'Back.easeOut',
          onComplete: () => {
            if (img.active) img.setScale(base);
          },
        });
      }
    }
    if (transient) {
      // Arm a single revert-to-base timer; a newer transient supersedes it via the token so a rapid
      // chomp streak reverts exactly once, to the CURRENT base (idle unless the rival is leading).
      const token = (Number(c.getData('__poseRevertToken')) || 0) + 1;
      c.setData('__poseRevertToken', token);
      this.time.delayedCall(600, () => {
        if (!c.active || c.getData('__poseRevertToken') !== token) return;
        this.setRivalPose(this.rivalBasePose);
      });
    }
  }


  /**
   * Colorblind double-coding: tag each muncher with a tiny "YOU"/"AI" label so the two players are
   * distinguishable without relying on cyan-vs-magenta. Only drawn when colorblind assist is on so
   * the default look (and screenshots) are unchanged.
   */
export function addPlayerTag(this: GameScene, c: Phaser.GameObjects.Container, text: string, color: number, r: number): void {
    // Always-on YOU/AI label — the primary cue that distinguishes the two munchers (they share a body
    // silhouette), so it shows by default, not only under colorblind assist (which makes it bolder).
    const big = colorblindActive();
    // Keep the tag on the lower half of the token. Above-token tags cut through the occupied cell's
    // answer label in tight phone/desktop captures, turning e.g. "Germany" into "GeYOUany".
    const tagY = this.spriteYOffset() + r * 0.28;
    const tag = this.add
      .text(0, tagY, text, {
        fontFamily: FONT.display,
        fontSize: `${Math.max(11, Math.min(big ? 14 : 12, r * (big ? 0.82 : 0.66)))}px`,
        color: hex(color),
        fontStyle: 'bold',
        stroke: hex(PALETTE.bg),
        strokeThickness: big ? 4 : 3,
      })
      .setOrigin(0.5, 0);
    c.add(tag);
  }


  /** Map a Core hazard kind to its archetype art key (falls back to the generic 'hazard'). */
export function hazardKeyFor(this: GameScene, kind?: string): Parameters<typeof tex>[0] {
    switch (kind) {
      case 'hunter':
        return 'hazardHunter';
      case 'crawler':
        return 'hazardCrawler';
      case 'liar':
        return 'hazardLiar';
      case 'scrambler':
        return 'hazardScrambler';
      default:
        return 'hazard';
    }
  }


export function makeHazardSprite(this: GameScene, kind?: string): Phaser.GameObjects.Container {
    const r = this.munchRadius() * 0.9;
    const key = this.hazardKeyFor(kind);
    let c: Phaser.GameObjects.Container;
    if (hasTex(key)) {
      c = this.makeArtSprite(key, r);
      c.setDepth(7);
    } else {
      c = this.makeProceduralHazard(kind, r);
    }
    addHazardNameChip.call(this, c, kind, r);
    return c;
  }


export function addHazardNameChip(
    this: GameScene,
    c: Phaser.GameObjects.Container,
    kind: string | undefined,
    r: number,
  ): void {
    const meta = HAZARD_LABEL[kind ?? ''];
    if (!meta) return;
    const big = colorblindActive();
    const fs = Math.max(12, Math.min(big ? 14 : 13, r * 0.68));
    const y = this.spriteYOffset() + r * 0.42;
    const text = this.add
      .text(0, y, meta.text, {
        fontFamily: FONT.display,
        fontSize: `${fs}px`,
        color: hex(PALETTE.white),
        fontStyle: 'bold',
        stroke: '#050915',
        strokeThickness: big ? 4 : 3,
      })
      .setOrigin(0.5);
    const bg = this.add.graphics();
    const padX = Math.max(4, fs * 0.46);
    const padY = Math.max(2, fs * 0.22);
    bg.fillStyle(PALETTE.void, 0.82);
    bg.fillRoundedRect(-text.width / 2 - padX, y - text.height / 2 - padY, text.width + padX * 2, text.height + padY * 2, 5);
    bg.lineStyle(1, meta.color, 0.72);
    bg.strokeRoundedRect(-text.width / 2 - padX, y - text.height / 2 - padY, text.width + padX * 2, text.height + padY * 2, 5);
    c.add([bg, text]);
  }


  /**
   * VG-7: PERSISTENT THREAT SIGNPOSTING. The munchers carry a cyan/magenta separation halo (YOU/AI);
   * a hazard had only a transient spawn/imminent-step telegraph, so at rest it could be mistaken for
   * the magenta AI rival. Mirror addSeparationGlow with a soft ADDITIVE RED halo (two concentric discs)
   * inserted BEHIND the hazard body so a hazard reads instantly as a steady "I am dangerous" threat —
   * unmistakable from the cyan player and magenta rival. This is the steady-state read; the spawn
   * `hazardTelegraph` stays as the separate arrival/imminent-step warning (red ring/flash on the cell),
   * so the two never double up. Subtle + behind-the-sprite + cell-bounded so it never hurts the
   * hazard's own legibility or bleeds onto neighbours. Motion-gated: a calm ~1.0-1.4s breathing pulse
   * normally, a SOLID (non-pulsing) ring under reduced-motion so the clarity survives reduced motion.
   * The pulse tween is tracked by hazard id (hazardAuraTweens) so it is killed cleanly on despawn/SHUTDOWN.
   */
export function addHazardDangerAura(this: GameScene, c: Phaser.GameObjects.Container, radius: number, hazardId: number): void {
    const yo = this.spriteYOffset();
    // Two concentric ADD discs (large/faint behind small/brighter) — same soft-rim recipe as the
    // separation halo, recoloured to the distinct HAZARD_THREAT orange (#40) so a hazard reads as its OWN
    // danger CLASS, unmistakable from the pink-red decoy-eat mistake flash. Base alphas chosen so the aura
    // reads clearly in BOTH modes (reduced-motion static uses these as-is; the motion pulse breathes around them).
    const BASE_OUTER = 0.3;
    const BASE_INNER = 0.4;
    const outer = this.add
      .circle(0, yo, radius * 1.45, HAZARD_THREAT, BASE_OUTER)
      .setBlendMode(Phaser.BlendModes.ADD);
    const inner = this.add
      .circle(0, yo, radius * 1.14, HAZARD_THREAT, BASE_INNER)
      .setBlendMode(Phaser.BlendModes.ADD);
    // #40 TARGETING RETICLE — four orange corner brackets ringing the hazard so it reads as a THREAT that
    // hunts you (a pre-strike "locked on" telegraph), a second non-hue cue that separates a hazard from a
    // mistake. Drawn once around the cell box; brackets stroke only, so the tile label stays readable.
    const e = radius * 1.5; // reticle half-extent (the cell-box corners)
    const arm = radius * 0.5; // bracket arm length
    const retic = this.add.graphics({ x: 0, y: yo });
    retic.lineStyle(2, HAZARD_THREAT, HAZARD_RETICLE_ALPHA);
    retic.beginPath(); retic.moveTo(-e, -e + arm); retic.lineTo(-e, -e); retic.lineTo(-e + arm, -e); retic.strokePath(); // TL
    retic.beginPath(); retic.moveTo(e - arm, -e); retic.lineTo(e, -e); retic.lineTo(e, -e + arm); retic.strokePath(); // TR
    retic.beginPath(); retic.moveTo(-e, e - arm); retic.lineTo(-e, e); retic.lineTo(-e + arm, e); retic.strokePath(); // BL
    retic.beginPath(); retic.moveTo(e - arm, e); retic.lineTo(e, e); retic.lineTo(e, e - arm); retic.strokePath(); // BR
    // Insert at index 1 (above the contact shadow at 0, below the backing/body) so the aura + reticle sit
    // BEHIND the hazard sprite and never occlude its shape or the cell label. Order the inserts so the
    // final stack is outer(faint) → inner(bright) → reticle(front), all still behind the body.
    c.addAt(retic, 1);
    c.addAt(inner, 1);
    c.addAt(outer, 1);
    if (!motionAllowed()) return; // reduced-motion: keep a SOLID orange aura + reticle (set above), no pulse.
    // Gentle "breathing" threat pulse — alpha + a hair of scale, slow yoyo so it reads as a living danger
    // glow, never a strobe. Drive a proxy 0->1 and pulse each element around its OWN faint base — tweening
    // `alpha` directly with a shared {from:1} would SNAP to full brightness on activation, blowing the design.
    const tween = this.tweens.add({
      targets: { v: 0 },
      v: 1,
      duration: 1200,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      onUpdate: (_tw, target: { v: number }) => {
        const k = 1 - 0.4 * target.v; // gentle breath: base -> 0.6 of base, always clearly visible
        const s = 1 + 0.08 * target.v; // scale: 1 -> 1.08
        if (outer.active) outer.setAlpha(BASE_OUTER * k).setScale(s);
        if (inner.active) inner.setAlpha(BASE_INNER * k).setScale(s);
        if (retic.active) retic.setAlpha(HAZARD_RETICLE_ALPHA * k).setScale(s);
      },
    });
    this.hazardAuraTweens.set(hazardId, tween);
  }


  /** Stop + drop a hazard's threat-aura pulse tween (no-op if none). Used on despawn + scene SHUTDOWN. */
export function removeHazardDangerAura(this: GameScene, hazardId: number): void {
    const tween = this.hazardAuraTweens.get(hazardId);
    if (tween) {
      tween.remove(); // the aura discs themselves die with the hazard container's destroy().
      this.hazardAuraTweens.delete(hazardId);
    }
  }


  /**
   * Procedural hazard fallback (no art). Each of the 4 Core hazard kinds reads DISTINCTLY by SHAPE +
   * accent so the player can tell them apart even with art off:
   *   hunter    — sharp red arrowhead (fast chaser)
   *   crawler   — rounded red blob (slow roamer)
   *   liar      — magenta diamond with a ⚠ (relabels answers)
   *   scrambler — scan-blue pinwheel/star (shuffles the board)
   * Returns a depth-7 container positioned at the sprite y-offset like the art path.
   */
export function makeProceduralHazard(this: GameScene, kind: string | undefined, r: number): Phaser.GameObjects.Container {
    const c = this.add.container(0, 0).setDepth(7);
    const yo = this.spriteYOffset();
    this.addSpriteShadow(c, r); // contact shadow first (behind the hazard body) so it grounds on the board.
    const ring = this.add.circle(0, yo, r + 3, PALETTE.bg, 0.85);
    c.add(ring);
    switch (kind) {
      case 'hunter': {
        // sharp upward arrowhead — reads as a fast, directed chaser.
        const head = this.add
          .triangle(0, yo, 0, -r, r, r * 0.9, -r, r * 0.9, HAZARD_THREAT) // #40 orange threat identity
          .setStrokeStyle(2, 0xffffff, 0.7);
        c.add(head);
        break;
      }
      case 'crawler': {
        // rounded blob — reads as a slow roamer; a darker core gives it an "eye".
        const body = this.add.circle(0, yo, r, HAZARD_THREAT).setStrokeStyle(2, 0xffffff, 0.5); // #40 orange threat
        const core = this.add.circle(0, yo, r * 0.42, PALETTE.bg, 0.85);
        c.add([body, core]);
        break;
      }
      case 'liar': {
        // magenta diamond + ⚠ — matches the lying-cell tell colour so the threat is recognisable.
        const diamond = this.add
          .polygon(0, yo, [0, -r, r, 0, 0, r, -r, 0], PALETTE.ai)
          .setStrokeStyle(2, 0xffffff, 0.6);
        const warn = this.add
          .text(0, yo, '⚠', {
            fontFamily: FONT.family,
            fontSize: `${Math.max(11, r)}px`,
            color: hex(PALETTE.armed),
            fontStyle: 'bold',
          })
          .setOrigin(0.5);
        c.add([diamond, warn]);
        break;
      }
      case 'scrambler': {
        // scan-blue 6-point star/pinwheel — reads as "shuffling / chaotic".
        const pts: number[] = [];
        const spikes = 6;
        for (let i = 0; i < spikes * 2; i++) {
          const rad = i % 2 === 0 ? r : r * 0.45;
          const ang = (Math.PI / spikes) * i - Math.PI / 2;
          pts.push(Math.cos(ang) * rad, Math.sin(ang) * rad);
        }
        const star = this.add.polygon(0, yo, pts, PALETTE.scan).setStrokeStyle(2, 0xffffff, 0.6);
        // slow spin so it reads as actively scrambling — static under reduced-motion (the 6-point
        // pinwheel SHAPE already distinguishes the scrambler from other hazards; the spin is decor).
        if (motionAllowed()) {
          this.tweens.add({ targets: star, angle: 360, duration: 2600, repeat: -1, ease: 'Linear' });
        }
        c.add(star);
        break;
      }
      default: {
        const diamond = this.add
          .polygon(0, yo, [0, -r, r, 0, 0, r, -r, 0], HAZARD_THREAT) // #40 orange threat identity
          .setStrokeStyle(2, 0xffffff, 0.6);
        c.add(diamond);
        break;
      }
    }
    return c;
  }

export function playMoveStep(this: GameScene): void {
    const now = this.time.now;
    if (this.lastMoveStepMs >= 0 && now - this.lastMoveStepMs < 70) return;
    this.lastMoveStepMs = now;
    // #61: move-step asset re-mastered to −22.65 LUFS; 0.5 play-gain × 0.9 SFX bus ≈ −29.6 LUFS
    // effective (+5 dB vs the old 0.28) — deliberately the quietest verb in the mix; it fires every
    // step. Pitch jitter keeps the highest-frequency verb from machine-gunning into a single tone.
    audio().playSfx('moveStep', { volume: 0.5, rate: 0.96 + Math.random() * 0.08 });
  }


export function moveSprite(this: GameScene,
    sprite: Phaser.GameObjects.Container,
    cellId: number,
    immediate: boolean,
    stunned: boolean,
  ): void {
    const c = this.cellCenter(cellId);
    const trailColor = sprite === this.aiSprite ? PALETTE.ai : sprite === this.humanSprite ? PALETTE.player : null;
    const isAi = sprite === this.aiSprite;
    sprite.setAlpha(stunned ? 0.45 : 1);
    if (immediate) {
      sprite.setPosition(c.x, c.y);
      sprite.setData('__activeMoveCell', null);
      if (isAi) {
        sprite.setData('__aiWindupCell', null);
        sprite.setData('__aiWindupUntil', 0);
      }
      if (trailColor !== null) {
        sprite.setData('__trailCell', cellId);
        this.trailEmitterFor(sprite, false)?.stop(); // a snap/respawn parks any streaming comet
      }
      return;
    }
    const dist = Phaser.Math.Distance.Between(sprite.x, sprite.y, c.x, c.y);
    if (dist < 1) {
      sprite.setData('__activeMoveCell', null);
      if (isAi) {
        sprite.setData('__aiWindupCell', null);
        sprite.setData('__aiWindupUntil', 0);
      }
      return;
    }
    if (isAi && motionAllowed()) {
      const windupCell = sprite.getData('__aiWindupCell') as number | null | undefined;
      const windupUntil = Number(sprite.getData('__aiWindupUntil') ?? 0);
      if (windupCell !== cellId || this.time.now > windupUntil + RIVAL_MOVE_TELEGRAPH_MS) {
        sprite.setData('__aiWindupCell', cellId);
        sprite.setData('__aiWindupUntil', this.time.now + RIVAL_MOVE_TELEGRAPH_MS);
        this.showRivalMoveTelegraph(sprite.x, sprite.y + this.spriteYOffset(), c.x, c.y + this.spriteYOffset(), cellId);
        return;
      }
      if (this.time.now < windupUntil) return;
    }
    if (sprite.getData('__activeMoveCell') === cellId) return;
    // Snap true teleports/respawns, but keep the rival interpolated so penalties never read as a teleport.
    if (dist > this.cellW * 1.6 && !isAi) {
      sprite.setPosition(c.x, c.y);
      sprite.setData('__activeMoveCell', null);
      if (trailColor !== null) {
        sprite.setData('__trailCell', cellId);
        this.trailEmitterFor(sprite, false)?.stop();
      }
    } else {
      const aiDuration = Math.min(240, Math.max(this.boardIndex >= 1 ? 135 : 155, dist * 1.15));
      const stepDuration = isAi ? aiDuration : sprite === this.humanSprite ? 160 : 110;
      // A1 comet trail: `stopSeq` is bumped ONCE per cell step (not on the per-frame homing re-adds
      // below), and only the matching tween-complete parks the emitter — so a queued multi-cell
      // glide keeps one continuous stream and an older step can never cut a newer one short.
      let stopSeq = -1;
      if (trailColor !== null && sprite.getData('__trailCell') !== cellId) {
        sprite.setData('__trailCell', cellId);
        this.emitMoveTrail(sprite, trailColor, c, isAi || sprite === this.humanSprite || dist > this.cellW * 1.6);
        this.pulseMoveSpriteFrame(sprite, cellId, stepDuration);
        this.pulseMoveSquash(sprite, cellId, stepDuration);
        if (sprite === this.humanSprite) this.playMoveStep();
        stopSeq = ((sprite.getData('__trailSeq') as number | undefined) ?? 0) + 1;
        sprite.setData('__trailSeq', stopSeq);
      }
      sprite.setData('__activeMoveCell', cellId);
      this.tweens.add({
        targets: sprite,
        x: c.x,
        y: c.y,
        duration: stepDuration,
        ease: 'Cubic.easeOut',
        onComplete: () => {
          if (sprite.getData('__activeMoveCell') === cellId) sprite.setData('__activeMoveCell', null);
          if (stopSeq >= 0 && sprite.getData('__trailSeq') === stopSeq) this.trailEmitterFor(sprite, false)?.stop();
          if (sprite === this.humanSprite && motionAllowed()) {
            shockwaveRing(this, c.x, c.y + this.spriteYOffset(), PALETTE.player, {
              maxRadius: this.munchRadius() * 1.55,
              durationMs: 210,
              thickness: Math.max(2, this.cellW * 0.045),
              peakAlpha: 0.34,
            });
          }
          // Rival presence: a brief magenta landing pulse when the rival settles on a cell,
          // so its steps register as intentional beats (throttled; below-sprite depth).
          if (isAi && sprite.getData('__trailCell') === cellId) this.pulseRivalArrival(c.x, c.y);
        },
      });
    }
  }


  /**
   * Rival MOVE TELEGRAPH — a compact magenta route line + target ring that lands before BYT3 starts
   * moving. This is presentation-only: Core has already emitted the new cell, but the renderer holds the
   * token for a readable wind-up so the rival feels intentional instead of teleport-reactive.
   */
export function showRivalMoveTelegraph(this: GameScene,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    cellId: number,
    holdMs = RIVAL_MOVE_TELEGRAPH_MS,
  ): void {
    if (!motionAllowed() || !this.aiEnabled || !this.aiSprite?.visible || !this.aiSprite.active) return;
    const v = this.cellViews[cellId];
    if (!v || !v.container.active) return;
    const pad = Math.max(4, Math.min(v.w, v.h) * 0.08);
    const left = toX - v.w / 2 + pad;
    const top = toY - v.h / 2 + pad;
    const w = v.w - pad * 2;
    const h = v.h - pad * 2;
    const g = this.add.graphics().setDepth(4.72).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.92);
    const drawDash = (thickness: number, color: number, alpha: number): void => {
      const dx = toX - fromX;
      const dy = toY - fromY;
      const dist = Math.max(1, Math.hypot(dx, dy));
      const dash = Math.max(8, this.cellW * 0.14);
      const gap = Math.max(6, this.cellW * 0.1);
      g.lineStyle(thickness, color, alpha);
      for (let d = 0; d < dist; d += dash + gap) {
        const a = d / dist;
        const b = Math.min(1, (d + dash) / dist);
        g.lineBetween(fromX + dx * a, fromY + dy * a, fromX + dx * b, fromY + dy * b);
      }
    };
    drawDash(Math.max(5, this.cellW * 0.075), 0x0a0b12, 0.84);
    drawDash(Math.max(3, this.cellW * 0.045), PALETTE.ai, 0.98);
    g.lineStyle(Math.max(2.5, this.cellW * 0.035), PALETTE.ai, 0.98);
    g.strokeRoundedRect(left, top, w, h, 8);

    const angle = Phaser.Math.Angle.Between(fromX, fromY, toX, toY);
    const arrowR = Math.max(6, Math.min(10, this.cellW * 0.14));
    const ax = toX - Math.cos(angle) * arrowR * 1.3;
    const ay = toY - Math.sin(angle) * arrowR * 1.3;
    const side = angle + Math.PI / 2;
    g.fillStyle(PALETTE.white, 0.94);
    g.fillTriangle(
      ax + Math.cos(angle) * arrowR,
      ay + Math.sin(angle) * arrowR,
      ax - Math.cos(angle) * arrowR * 0.65 + Math.cos(side) * arrowR * 0.62,
      ay - Math.sin(angle) * arrowR * 0.65 + Math.sin(side) * arrowR * 0.62,
      ax - Math.cos(angle) * arrowR * 0.65 - Math.cos(side) * arrowR * 0.62,
      ay - Math.sin(angle) * arrowR * 0.65 - Math.sin(side) * arrowR * 0.62,
    );

    this.tweens.add({
      targets: g,
      alpha: { from: 0.92, to: 0.44 },
      scaleX: { from: 1, to: 1.02 },
      scaleY: { from: 1, to: 1.02 },
      duration: 120,
      yoyo: true,
      hold: Math.max(0, holdMs - 240),
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: g,
          alpha: 0,
          duration: 140,
          ease: 'Quad.easeIn',
          onComplete: () => g.destroy(),
        });
      },
    });
  }


  /**
   * Rival ARRIVAL pulse — a small magenta ring that blooms once where the rival lands. Decoration
   * only (the intent pulse + comet trail stay the persistent cues): throttled to ~1 ring / 420ms so
   * a fast rival run never sprays, parked at depth 4.6 (below both munchers at 5/6, same additive
   * band as the existing move ghost) so it can never occlude a label harder than the move cue
   * already does. Motion-gated with NO static fallback — under reduced-motion the rival's solid
   * intent pulse ring already carries "the rival acted", and a repeating appear/fade ring would
   * itself be motion.
   */
export function pulseRivalArrival(this: GameScene, x: number, y: number): void {
    if (!motionAllowed() || this.time.now < this.nextRivalArrivalMs) return;
    if (!this.aiSprite?.visible || !this.aiSprite.active) return;
    this.nextRivalArrivalMs = this.time.now + 420;
    const r = this.munchRadius();
    const ring = this.add
      .circle(x, y + this.spriteYOffset(), r * 0.85)
      .setStrokeStyle(2, PALETTE.ai, 1)
      .setFillStyle()
      .setDepth(4.6)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0.55);
    this.tweens.add({
      targets: ring,
      radius: r * 1.55,
      alpha: 0,
      duration: 300,
      ease: 'Quad.easeOut',
      onComplete: () => ring.destroy(),
    });
  }


  /**
   * A1: the pooled per-muncher comet-trail emitter. Created ONCE (lazily, when `create` is true on
   * the first real move) and reused across every move — player tinted cyan, rival magenta and a
   * touch weaker. Depths sit UNDER each muncher (5 / 6) and over the tiles, matching the existing
   * line-trail band, so the streak never occludes labels harder than the current move cue. `null`
   * is cached when the dot texture is unavailable (headless) so we never re-try per move.
   */
export function trailEmitterFor(this: GameScene,
    sprite: Phaser.GameObjects.Container,
    create: boolean,
  ): Phaser.GameObjects.Particles.ParticleEmitter | null {
    const isAi = sprite === this.aiSprite;
    const cached = isAi ? this.aiTrailEmitter : this.playerTrailEmitter;
    if (cached !== undefined) return cached && cached.active ? cached : null;
    if (!create) return null;
    const emitter = createCometTrail(
      this,
      isAi ? PALETTE.ai : PALETTE.player,
      isAi
        ? // Rival presence: one notch up from {0.3 / 0.5 / 40ms} so the magenta streak reads in
          // capture — still clearly weaker than the player's own trail (the hero keeps the hero cue).
          { depth: 4.7, scaleStart: 0.36, alphaStart: 0.6, frequency: 32 }
        : // 4.9 keeps the player trail under the rival sprite (5) so cyan sparks never cross over it.
          { depth: 4.9, scaleStart: 0.42, alphaStart: 0.7, frequency: 26 },
    );
    if (isAi) this.aiTrailEmitter = emitter;
    else this.playerTrailEmitter = emitter;
    return emitter;
  }


export function emitMoveTrail(this: GameScene,
    sprite: Phaser.GameObjects.Container,
    color: number,
    to?: { x: number; y: number },
    strong = false,
  ): void {
    if (!motionAllowed() || !sprite.visible || !sprite.active) return;
    const r = this.munchRadius();
    const y = sprite.y + this.spriteYOffset();
    if (to) {
      // A1: stream the pooled comet trail while the sprite is actually tweening between cells —
      // moveSprite's tween completion stops it. Reduced-motion never reaches here (gate above), so
      // vestibular users keep a completely static move read.
      const comet = this.trailEmitterFor(sprite, true);
      if (comet) {
        comet.startFollow(sprite, 0, this.spriteYOffset());
        comet.start();
      }
      const targetY = to.y + this.spriteYOffset();
      const path = this.add.graphics().setDepth(strong ? 5.2 : 4.8).setBlendMode(Phaser.BlendModes.ADD);
      path.lineStyle(strong ? Math.max(5, this.cellW * 0.11) : 2, color, strong ? 0.72 : 0.3);
      path.lineBetween(sprite.x, y, to.x, targetY);
      if (strong) {
        path.lineStyle(Math.max(2, this.cellW * 0.045), PALETTE.white, 0.28);
        path.lineBetween(sprite.x, y, to.x, targetY);
      }
      path.fillStyle(color, strong ? 0.42 : 0.26);
      path.fillCircle(to.x, targetY, r * (strong ? 0.46 : 0.24));
      this.tweens.add({
        targets: path,
        alpha: 0,
        duration: strong ? 380 : 260,
        ease: 'Quad.easeOut',
        onComplete: () => path.destroy(),
      });
    }
    const ghost = this.add
      .circle(sprite.x, y, r * (strong ? 1.18 : 0.9), color, strong ? 0.48 : 0.24)
      .setDepth(strong ? 5.1 : 4.6)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({
      targets: ghost,
      alpha: 0,
      scale: strong ? 2.05 : 1.65,
      duration: strong ? 420 : 260,
      ease: 'Quad.easeOut',
      onComplete: () => ghost.destroy(),
    });
  }


export function updateAiPressureCue(this: GameScene, snap: Snapshot): void {
    const g = this.aiPressureCue;
    if (!g) return;
    g.clear();
    if (!this.aiEnabled || snap.status !== 'playing') return;
    const ai = snap.players.find((p) => p.id === AI);
    if (!ai || !ai.alive) return;

    const here = this.cellCenter(ai.pos);
    const y = here.y + this.spriteYOffset();
    const pulse = motionAllowed() ? 0.5 + 0.5 * Math.sin(this.time.now / 260) : 0.5;
    const alpha = (this.boardIndex >= 1 ? 0.34 : 0.26) + (this.boardIndex >= 1 ? 0.3 : 0.24) * pulse;
    const dotR = Math.max(3, Math.min(5, this.cellW * 0.07));
    const row = Math.floor(ai.pos / this.cols);
    const col = ai.pos % this.cols;
    const candidates = [
      [row - 1, col],
      [row + 1, col],
      [row, col - 1],
      [row, col + 1],
    ];

    g.lineStyle(3, PALETTE.ai, alpha);
    g.strokeCircle(here.x, y, Math.max(12, this.munchRadius() * 1.36));

    for (const [r, c] of candidates) {
      if (r < 0 || c < 0 || r >= this.rows || c >= this.cols) continue;
      const id = r * this.cols + c;
      const cell = snap.cells[id];
      if (!cell || cell.kind === 'wall' || cell.eaten || cell.armed) continue;
      const to = this.cellCenter(id);
      const midX = here.x + (to.x - here.x) * 0.42;
      const midY = y + (to.y - here.y) * 0.42;
      g.lineStyle(2.5, PALETTE.ai, alpha * 0.78);
      g.beginPath();
      g.moveTo(here.x, y);
      g.lineTo(midX, midY);
      g.strokePath();
      g.fillStyle(PALETTE.ai, alpha * 0.9);
      g.fillCircle(midX, midY, dotR);
      g.lineStyle(1.5, PALETTE.ai, alpha * 0.72);
      g.strokeCircle(to.x, to.y, Math.max(6, dotR * 1.55));
    }
  }


export function showRivalMunchBeat(this: GameScene, at: { x: number; y: number }, success: boolean, value?: number, cellId?: number): void {
    if (!this.aiEnabled) return;
    const y = at.y + this.spriteYOffset();
    const r = this.munchRadius();
    const color = success ? PALETTE.ai : PALETTE.hazard;
    const decoyBeat = !success;
    const rivalFocusX = decoyBeat && this.aiSprite?.active ? this.aiSprite.x : at.x;
    const rivalFocusY = decoyBeat && this.aiSprite?.active ? this.aiSprite.y + this.spriteYOffset() : y;
    const markerX = decoyBeat ? rivalFocusX : at.x;
    const markerY = decoyBeat ? rivalFocusY : y;
    const marker = this.add.container(markerX, markerY).setDepth(8.6).setAlpha(0.98);
    if (decoyBeat) {
      this.rivalReadout?.setData('suppressThinkingUntil', this.time.now + 1900);
      this.rivalThinkingText?.setVisible(false);
      this.rivalPersona?.setThinking(false);
    }
    // Dark backing disc UNDER the additive body so the beat reads as a discrete layer ABOVE the grid
    // (Gemini: "add a semi-transparent backing to separate it from the grid lines") instead of blending
    // into the neighbouring tile borders.
    // Lighter dark backing on the wrong-eat beat (Gemini MINOR) so the answer label reads through it.
    const backing = this.add.circle(0, 0, r * 0.98, 0x0a0b12, 0.4).setVisible(!decoyBeat);
    const body = this.add
      .circle(
        0,
        0,
        r * 0.92,
        PALETTE.ai,
        0.2,
      )
      .setStrokeStyle(3, color, 0.95)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(!decoyBeat);
    const glyph = this.add
      .text(0, 0, success ? '✓' : '×', {
        fontFamily: FONT.display,
        // Gemini MINOR: the RIVAL-WRONG × used to blanket the answer label. Smaller glyph + softer alpha
        // keeps the × shape (colorblind double-code) while the tile text stays readable under it.
        fontSize: `${Math.max(decoyBeat ? 20 : 18, r * (decoyBeat ? 1.12 : 1.28))}px`,
        color: hex(color),
        fontStyle: 'bold',
        stroke: '#0a0b12',
        strokeThickness: decoyBeat ? 5 : 4,
      })
      .setOrigin(0.5)
      .setAlpha(decoyBeat ? 0 : 1)
      .setVisible(!decoyBeat);
    // #40: the plated marker carries the penalty inline so it is the ONE decoy-eat label (see the float dedupe below).
    // Keep the plate compact: the large X/pose/bark already carry "wrong", and a wide banner blankets
    // neighboring answer labels on the landscape board.
    const wrongLabel = Math.abs(value ?? 0) > 0 ? `−${Math.abs(value ?? 0)}` : 'MISS';
    const wrongText = decoyBeat
      ? this.add
          .text(0, 0, wrongLabel, {
            fontFamily: FONT.display,
            fontSize: `${Math.max(13, Math.min(15, r * 0.78))}px`,
            color: hex(PALETTE.white),
            fontStyle: 'bold',
            stroke: '#0a0b12',
            strokeThickness: 4,
          })
          .setOrigin(0.5)
      : undefined;
    const wrongBg =
      wrongText !== undefined
        ? this.add
            .rectangle(wrongText.x, wrongText.y, 52, 28, 0x0a0b12, 0.82)
            .setStrokeStyle(2, PALETTE.hazard, 0.95)
        : undefined;
    marker.add(wrongText && wrongBg ? [backing, body, glyph, wrongBg, wrongText] : [backing, body, glyph]);
    if (decoyBeat && cellId !== undefined) {
      const v = this.cellViews[cellId];
      if (v?.container.active) {
        glowPulse(this, v.container, v.w, v.h, PALETTE.hazard, 1100);
        const tileX = this.add.graphics().setDepth(8.05);
        tileX.fillStyle(PALETTE.hazard, 0.16);
        tileX.fillRoundedRect(at.x - v.w / 2 + 4, at.y - v.h / 2 + 4, v.w - 8, v.h - 8, 8);
        tileX.lineStyle(2, PALETTE.hazard, 0.72);
        tileX.strokeRoundedRect(at.x - v.w / 2 + 4, at.y - v.h / 2 + 4, v.w - 8, v.h - 8, 8);
        const link = this.add.graphics().setDepth(8.04);
        const dx = at.x - rivalFocusX;
        const dy = at.y - rivalFocusY;
        const dist = Math.max(1, Math.hypot(dx, dy));
        const steps = Math.max(5, Math.floor(dist / 14));
        const drawDash = (width: number, color: number, alpha: number): void => {
          link.lineStyle(width, color, alpha);
          for (let i = 0; i < steps; i += 2) {
            const a = i / steps;
            const b = Math.min(1, (i + 1) / steps);
            link.beginPath();
            link.moveTo(rivalFocusX + dx * a, rivalFocusY + dy * a);
            link.lineTo(rivalFocusX + dx * b, rivalFocusY + dy * b);
            link.strokePath();
          }
        };
        drawDash(8, 0x0a0b12, 0.92);
        drawDash(5, PALETTE.ai, 1);
        this.tweens.add({
          targets: [tileX, link],
          alpha: 0,
          duration: 1180,
          ease: 'Quad.easeOut',
          onComplete: () => {
            tileX.destroy();
            link.destroy();
          },
        });
      }
      particleBurst(this, at.x, y, PALETTE.hazard, 18);
      shockwaveRing(this, at.x, y, PALETTE.hazard, {
        maxRadius: this.cellW * 1.55,
        durationMs: 360,
        thickness: Math.max(3, this.cellW * 0.08),
        peakAlpha: 0.5,
      });
      shake(this.cameras.main, 6, 150);
      // Rival misses should feel like BYT3 embarrassment, not player damage. Keep the tile-local wrong
      // mark red, but pulse the screen edge magenta behind UI while excluding the rule strip.
      rivalMissEdgePulse(this);
      particleBurst(this, rivalFocusX, rivalFocusY, PALETTE.ai, 10);
      shockwaveRing(this, rivalFocusX, rivalFocusY, PALETTE.ai, {
        maxRadius: r * 1.7,
        durationMs: 420,
        thickness: Math.max(2, r * 0.16),
        peakAlpha: 0.56,
      });
      floatText(this, rivalFocusX, rivalFocusY - r * 1.65, 'BYT3 MISS', hex(PALETTE.ai), Math.max(13, Math.min(16, r * 0.78)), {
        clearance: 'high',
      });
      if (this.rivalReadout?.active) {
        const land = this.scale.width > this.scale.height;
        const floorBg = this.add.graphics();
        const floorW = land ? 70 : 76;
        const floorH = 22;
        floorBg.fillStyle(0x0a0b12, 0.92);
        floorBg.fillRoundedRect(-floorW / 2, -floorH / 2, floorW, floorH, 9);
        floorBg.lineStyle(1.25, PALETTE.ai, 0.85);
        floorBg.strokeRoundedRect(-floorW / 2, -floorH / 2, floorW, floorH, 9);
        const floorText = this.add
          .text(0, 0, '0 FLOOR', {
            fontFamily: FONT.display,
            fontSize: `${land ? 12 : 14}px`,
            color: '#ffb020',
            fontStyle: 'bold',
            stroke: '#0a0b12',
            strokeThickness: 3,
          })
          .setOrigin(0.5);
        const floor = this.add
          .container(land ? 36 : 0, land ? -36 : 30, [floorBg, floorText])
          .setAlpha(0);
        this.rivalReadout.add(floor);
        if (motionAllowed()) {
          this.tweens.add({ targets: floor, alpha: 1, y: floor.y - 3, duration: 160, ease: 'Quad.easeOut' });
          this.tweens.add({
            targets: floor,
            alpha: 0,
            y: floor.y + 3,
            delay: 1350,
            duration: 260,
            ease: 'Quad.easeIn',
            onComplete: () => floor.destroy(),
          });
        } else {
          floor.setAlpha(1);
          this.time.delayedCall(1600, () => floor.destroy());
        }
      }
    }

    // Ruling 6 (hand off, don't stack): the RIVAL WRONG beat + its readout portrait pulse now OWN the
    // rival focal point — so hand off from the persistent grid rival-token cue (aiPressureCue) for the
    // beat window. REUSE the broadcast focal-suppression machinery (no second mechanism) so the screen
    // never reads as two rivals emoting at once. No-op when the broadcast package isn't built (ladder/solo).
    if (decoyBeat) this.broadcast?.focalSuppressForWrongBeat();

    if (this.aiSprite?.visible && this.aiSprite.active) {
      squashStretch(this, this.aiSprite, { amount: success ? 0.22 : 0.18 });
      // S6: the pose token snaps to its aggressive CHOMP frame for ~600ms, then reverts to the base
      // pose (idle, or taunt while it leads). No-op on the spritesheet/triangle fallback (below).
      this.setRivalPose('chomp', true);
      this.pulseSpriteFrame(this.aiSprite, 'aiChomp', 'aiIdle', decoyBeat ? 240 : 150);
      // Animated raster rival (when present): a quick aggressive "advance" dart on its eat.
      this.playMuncherAnim(this.aiSprite, 'mun-rival-advance');
    }

    // The rival score delta now lives in the dedicated rival readout (hudController.updateRivalReadout).
    // Keeping a second board-cell "+N" float covered live answer labels in the exact combo-read states
    // players must parse fastest. The marker/check/particles here still identify which cell BYT3 ate.
    if (this.rivalReadout?.active && !decoyBeat) {
      glowPulse(this, this.rivalReadout, 120, 34, color, 420);
    }

    if (!motionAllowed()) {
      this.time.delayedCall(decoyBeat ? 1700 : 240, () => marker.destroy());
      return;
    }
    marker.setScale(decoyBeat ? 0.5 : 0.78);
    if (decoyBeat) {
      marker.setAlpha(0);
      this.tweens.add({
        targets: marker,
        alpha: 0.98,
        scale: 1,
        duration: 210,
        ease: 'Back.easeOut',
        onComplete: () => {
          if (!marker.active) return;
          this.tweens.add({
            targets: marker,
            scale: 1,
            alpha: 0,
            delay: 1280,
            duration: 380,
            ease: 'Quad.easeIn',
            onComplete: () => marker.destroy(),
          });
        },
      });
      return;
    }
    this.tweens.add({
      targets: marker,
      scale: 1.35,
      alpha: 0,
      duration: 420,
      ease: 'Quad.easeOut',
      onComplete: () => marker.destroy(),
    });
  }


  /**
   * VG-10: DEATH → RESPAWN beat. A life loss makes Core snap the human back to its start cell; on its
   * own that reads as a glitchy teleport. This dresses the (already-applied) reposition as a deliberate
   * two-part beat so the loss is legible WHERE it happened and the arrival reads as intentional:
   *   1) a DEATH cue at the muncher's PRE-RESET screen position (fromX/fromY): a hazard-red particle
   *      burst + a short-lived "ghost" disc that fades + shrinks (the muncher visibly "pops" off there),
   *   2) a RESPAWN cue on the REAL sprite at its new (already-snapped, authoritative) cell: a quick
   *      materialize (SCALE 0.6→1.0) plus a soft cyan glow ring + burst so it arrives on purpose.
   * The real sprite's position is never touched here (moveSprite already placed it) — only its scale is
   * tweened back to 1, so the authoritative position can never desync. Under reduced-motion we
   * skip the tweens but keep the loss legible: a calm static red marker at the death spot + a static
   * cyan ring at the respawn spot (no strobe, no scale pop). Transient objects are tracked in
   * respawnCueObjs and self-prune on completion (and are force-cleaned on SHUTDOWN).
   */
export function playRespawnCue(this: GameScene, fromX: number, fromY: number): void {
    const r = this.munchRadius();
    const toX = this.humanSprite.x;
    const toY = this.humanSprite.y + this.spriteYOffset();
    const reduced = !motionAllowed();

    // 1) DEATH cue at the pre-reset spot — burst + a fading ghost so the loss reads where it happened.
    particleBurst(this, fromX, fromY + this.spriteYOffset(), PALETTE.hazard, reduced ? 3 : 12);
    const ghost = this.add
      .circle(fromX, fromY + this.spriteYOffset(), r, PALETTE.hazard, reduced ? 0.5 : 0.85)
      .setDepth(7);
    this.respawnCueObjs.add(ghost);
    const dropGhost = (): void => {
      this.respawnCueObjs.delete(ghost);
      ghost.destroy();
    };
    if (reduced) {
      // calm static marker — hold briefly, then remove (no shrink/strobe).
      this.time.delayedCall(420, dropGhost);
    } else {
      this.tweens.add({
        targets: ghost,
        scale: 0.2,
        alpha: 0,
        duration: 260,
        ease: 'Quad.easeIn',
        onComplete: dropGhost,
      });
    }

    // 2) RESPAWN cue on the REAL sprite — a materialize pop + a soft cyan ring so the arrival is a beat.
    const ring = this.add.circle(toX, toY, r * 1.5, PALETTE.player, 0).setStrokeStyle(3, PALETTE.player, reduced ? 0.7 : 1);
    ring.isFilled = false;
    ring.setDepth(7);
    this.respawnCueObjs.add(ring);
    const dropRing = (): void => {
      this.respawnCueObjs.delete(ring);
      ring.destroy();
    };
    if (reduced) {
      // static cyan ring marking the new spot; the sprite holds full scale (no animation).
      this.time.delayedCall(420, dropRing);
    } else {
      // Materialize the sprite in: pop its SCALE from a small ghost back to full presence at its real
      // cell. We drive SCALE only (not alpha) — moveSprite() owns the sprite's alpha every frame (it
      // dims to 0.45 during the post-respawn stun), so animating alpha here would just be clobbered and
      // could desync the dim; the 0.45 stun-dim itself reads as a fitting "just respawned" invuln flash.
      this.humanSprite.setScale(0.6);
      this.tweens.add({
        targets: this.humanSprite,
        scale: 1,
        duration: 240,
        delay: 120, // let the death ghost read first, then the respawn pops in just after.
        ease: 'Back.easeOut',
        onComplete: () => {
          if (this.humanSprite.active) this.humanSprite.setScale(1); // settle exactly to base.
        },
      });
      particleBurst(this, toX, toY, PALETTE.player, 8);
      this.tweens.add({
        targets: ring,
        scaleX: 1.6,
        scaleY: 1.6,
        alpha: { from: 1, to: 0 },
        duration: 420,
        delay: 120,
        ease: 'Quad.easeOut',
        onComplete: dropRing,
      });
    }
  }


  /**
   * True while the human is immune to hazards this frame — standing on SAFE GROUND (an eaten-correct
   * tile) OR mid CHOMP SURGE. Mirrors Core's resolveHazardHits skip/purge so the renderer never cries
   * "danger" about a threat the player can't actually be hurt by (used to gate the proximity warnings).
   */
export function isHumanProtected(this: GameScene, snap: Snapshot, human: Snapshot['players'][number]): boolean {
    if (!human.alive) return false;
    if (snap.timeMs < human.surgeUntilMs) return true; // surge purges on contact
    const cell = snap.cells[human.pos];
    return !!cell && cell.eaten && cell.kind === 'correct'; // safe ground passes hazards through
  }


  /**
   * Toggle + animate the human's SAFE-GROUND shield and CHOMP-SURGE auras from the live snapshot. Pure
   * render (reads snapshot state only). Motion-gated: a gentle breathe / hot pulse under motion, a plain
   * steady ring under reduced-motion. Also fires the one-shot SAFE-GROUND coach line the first time the
   * mechanic is actually relevant (on safe ground with a hazard on the board).
   */
export function updatePlayerAuras(this: GameScene, snap: Snapshot, human: Snapshot['players'][number]): void {
    const safe = this.safeGroundRing;
    const bubble = this.safeGroundBubble;
    const surge = this.surgeRing;
    const core = this.surgeCore;
    if (!safe || !bubble || !surge || !core || !safe.active || !surge.active) return;
    const motion = motionAllowed();

    // SAFE GROUND — standing on any eaten CORRECT cell (mirrors Core's owner-agnostic skip rule). Reads
    // as a translucent green shield DOME: a bright breathing rim + a semi-transparent fill bubble.
    const cell = snap.cells[human.pos];
    const onSafe = human.alive && !!cell && cell.eaten && cell.kind === 'correct';
    if (onSafe) {
      const b = motion ? 0.5 + 0.5 * Math.sin(this.time.now / 360) : 1; // calm breathe (static under reduceMotion)
      const s = motion ? 1 + 0.05 * b : 1;
      bubble.setVisible(true).setAlpha(0.14 + 0.1 * b).setScale(s);
      safe.setVisible(true).setAlpha(0.7 + 0.28 * b).setScale(s);
      // Teach the mechanic the first time it can matter (a hazard exists to be immune to). Once/session.
      if (!this.safeGroundCoachShown && snap.hazards.length > 0 && !this.registry.get('taughtSafeGround')) {
        this.safeGroundCoachShown = true;
        this.registry.set('taughtSafeGround', true);
        this.showCommandHint("Cleared tiles are SAFE GROUND — hazards can't touch you there.", PALETTE.correct, 4200);
      }
    } else {
      bubble.setVisible(false);
      safe.setVisible(false);
    }

    // CHOMP SURGE — an electric blue-white ring (white-hot edge over a deeper-blue core) that shrinks +
    // fades over the surge window (frac 1→0), plus the reused spark emitter for the crackle texture.
    const frac = Phaser.Math.Clamp((human.surgeUntilMs - snap.timeMs) / SCORING.surgeDurationMs, 0, 1);
    const surging = human.alive && frac > 0;
    if (surging) {
      if (motion) {
        const pulse = 0.5 + 0.5 * Math.sin(this.time.now / 80); // fast, charged flicker
        surge.setVisible(true).setAlpha((0.42 + 0.46 * frac) * (0.65 + 0.35 * pulse)).setScale(0.82 + 0.18 * frac);
        core.setVisible(true).setAlpha((0.18 + 0.22 * frac) * (0.6 + 0.4 * pulse)).setScale(0.9 + 0.12 * frac);
      } else {
        surge.setVisible(true).setAlpha(0.46 + 0.34 * frac).setScale(0.9 + 0.1 * frac);
        core.setVisible(true).setAlpha(0.22 + 0.2 * frac).setScale(1);
      }
    } else {
      surge.setVisible(false);
      core.setVisible(false);
    }
    this.updateSurgeSparks(surging && motion); // sparks are motion-only (the ring/core carry reduceMotion)
  }


  /** A tiny white spark/cross texture for the CHOMP-SURGE crackle emitter. Generated once, then cached. */
export function ensureSurgeSparkTexture(this: GameScene): string {
    const key = '__surgeSpark';
    if (this.textures.exists(key)) return key;
    try {
      const g = this.make.graphics({ x: 0, y: 0 });
      const S = 14;
      const c = S / 2;
      g.fillStyle(0xffffff, 1);
      g.fillRect(c - 0.9, 1, 1.8, S - 2); // vertical bar
      g.fillRect(1, c - 0.9, S - 2, 1.8); // horizontal bar
      g.fillCircle(c, c, 2); // bright core
      g.generateTexture(key, S, S);
      g.destroy();
    } catch {
      return '';
    }
    return key;
  }


  /**
   * Drive the persistent CHOMP-SURGE spark emitter: created lazily on first use, then REUSED (start/stop
   * only — no per-frame allocation). Follows the muncher and only emits while surging + motion allowed.
   */
export function updateSurgeSparks(this: GameScene, on: boolean): void {
    if (this.surgeSparks === null) return; // texture unavailable (headless) — ring/core carry the read
    if (!this.surgeSparks) {
      if (!on) return; // don't create the emitter until it's first needed
      const key = this.ensureSurgeSparkTexture();
      if (!key) {
        this.surgeSparks = null;
        return;
      }
      this.surgeSparks = this.add
        .particles(0, 0, key, {
          lifespan: { min: 160, max: 300 },
          speed: { min: 30, max: 110 },
          angle: { min: 0, max: 360 },
          scale: { start: 0.5, end: 0 },
          alpha: { start: 0.95, end: 0 },
          rotate: { min: 0, max: 360 },
          quantity: 1,
          frequency: 45,
          tint: SURGE_EDGE,
          blendMode: 'ADD',
          emitting: false,
        })
        .setDepth(6.6);
    }
    const em = this.surgeSparks;
    if (!em.active) return;
    if (on) {
      em.setPosition(this.humanSprite.x, this.humanSprite.y + this.spriteYOffset());
      if (!em.emitting) em.start();
    } else if (em.emitting) {
      em.stop();
    }
  }
